import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkle, CreditCard, CheckCircle } from '@phosphor-icons/react';
import { doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import {useFirebaseAuth} from "../context/FirebaseAuthContext";
import {db} from "../lib/firebase";
import {Button} from "../components/ui/button";
import {Card} from "../components/ui/card";
import {PaystackButton} from "react-paystack";
import {GoogleGenAI} from "@google/genai";
import {formatDate} from "../utils/helpers";
import SEO from "../seo/SEO";

const ECSA_OUTCOMES = [
    {
        number: 1,
        title: "Problem Solving",
        description: "Identify, formulate, analyze, and solve complex engineering problems creatively and innovatively."
    },
    {
        number: 2,
        title: "Application of scientific knowledge",
        description: "Apply knowledge of mathematics, natural science, engineering fundamentals and an engineering specialization to solve complex engineering problems."
    },
    {
        number: 3,
        title: "Engineering design",
        description: "Perform creative, procedural and non-procedural design of components, systems, works and products to meet user and societal needs."
    },
    {
        number: 4,
        title: "Investigations and experiments",
        description: "Conduct investigations of complex problems using research-based knowledge and research methods including design of experiments."
    },
    {
        number: 5,
        title: "Engineering methods and tools",
        description: "Select and apply appropriate engineering methods, skills, tools and techniques including synthesis and modelling, to solve complex engineering problems."
    },
    {
        number: 6,
        title: "Professional communication",
        description: "Communicate effectively on complex engineering activities with the engineering community and with society at large."
    },
    {
        number: 7,
        title: "Sustainability and impact",
        description: "Evaluate and address the impact of engineering activities on the environment and sustainable development."
    },
    {
        number: 8,
        title: "Engineering professionalism",
        description: "Demonstrate knowledge and understanding of engineering management principles and economic decision-making."
    },
    {
        number: 9,
        title: "Independent learning",
        description: "Demonstrate independent learning and the ability to engage in lifelong learning."
    },
    {
        number: 10,
        title: "Engineering judgment",
        description: "Demonstrate engineering judgment and decision-making in complex and uncertain situations."
    },
    {
        number: 11,
        title: "Project management",
        description: "Plan, organize and manage engineering projects and teams effectively."
    }
];

const AssessmentPage = () => {
    const { workExpId } = useParams();
    const navigate = useNavigate();
    const { user, userData } = useFirebaseAuth();
    const [workExp, setWorkExp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    const [pricing, setPricing] = useState({ assessment_price: 150, currency: 'ZAR' });
    const [checkingPayment, setCheckingPayment] = useState(true);
    const [paymentReference, setPaymentReference] = useState(null);
    const [assessmentResult, setAssessmentResult] = useState(null);

    // Initialize Google GenAI
    const ai = new GoogleGenAI({
        apiKey: process.env.REACT_APP_GOOGLE_AI_API_KEY || "AQ.Ab8RN6J2R0YqIa0vOix-j4rtDC6isXFHmcAUl4mEGQNpX43Yyg"
    });

    const config = {
        reference: (new Date()).getTime().toString(),
        email: user?.email || '',
        amount: pricing.assessment_price * 100, // Amount in cents/kobo
        publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_d1b62c4a430407c1a991be4d6c4b258b83b896b6',
        currency: pricing.currency,
    };

    useEffect(() => {
        if (user) {
            fetchInitialData();
        } else {
            navigate('/');
            toast.error('Please login to access this page');
        }
    }, [workExpId, user]);

    const handlePaystackSuccessAction = (reference) => {
        // Implementation for whatever you want to do with reference and after success call.
        handlePayment(reference);
    };

    // you can call this function anything
    const handlePaystackCloseAction = () => {
        // implementation for  whatever you want to do when the Paystack dialog closed.
        console.log('closed')
    }

    const componentProps = {
        ...config,
        text: `Pay R${pricing.assessment_price} with Paystack`,
        onSuccess: (reference) => handlePaystackSuccessAction(reference),
        onClose: handlePaystackCloseAction,
    };

    const fetchInitialData = async () => {
        if (!user) return;

        try {
            const workExpRef = doc(db, 'workExperiences', workExpId);
            const workExpDoc = await getDoc(workExpRef);

            if (!workExpDoc.exists()) {
                toast.error('Work experience not found');
                navigate('/dashboard');
                return;
            }

            const workExpData = { id: workExpDoc.id, ...workExpDoc.data() };

            if (workExpData.userId !== user.uid && userData?.role !== 'admin') {
                toast.error('You do not have permission to assess this work experience');
                navigate('/dashboard');
                return;
            }

            setWorkExp(workExpData);
            await checkPaymentStatus(workExpId);

        } catch (error) {
            console.error('Error fetching assessment data:', error);
            toast.error(error.message);
            navigate('/dashboard');
        } finally {
            setCheckingPayment(false);
        }
    };

    const checkPaymentStatus = async (workExpId) => {
        try {
            const paymentsRef = collection(db, 'payments');
            const paymentsQuery = query(
                paymentsRef,
                where('workExpId', '==', workExpId),
                where('userId', '==', user.uid),
                where('purpose', '==', 'assessment'),
                where('status', '==', 'completed')
            );
            const paymentsSnapshot = await getDocs(paymentsQuery);

            if (!paymentsSnapshot.empty) {
                setHasPaid(true);
                const payment = paymentsSnapshot.docs[0];
                setPaymentReference(payment.id);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
        }
    };

    const handlePayment = async (reference) => {
        // setPaying(true);
        try {
            const paymentData = {
                userId: user.uid,
                workExpId: workExpId,
                amount: pricing.assessment_price,
                currency: pricing.currency,
                purpose: 'assessment',
                status: 'completed',
                created_at: new Date().toISOString(),
                user_email: user.email,
                user_name: userData?.full_name || user.displayName,
                paymentReference: reference.reference
            };

            await addDoc(collection(db, 'payments'), paymentData);
            // setHasPaid(true);
            toast.success('Payment successful! You can now run the assessment.');
            // setPaying(false);

        } catch (error) {
            console.error('Payment initialization error:', error);
            toast.error('Failed to process payment. Please try again.');
            setPaying(false);
        }
    };

    // Gemini AI Assessment function
    const performAIAssessment = async (workExperience, userData) => {
        try {
            // Prepare assessment prompt
            const outcomesText = ECSA_OUTCOMES.map((o, i) =>
                `${i + 1}. ${o.title} - ${o.description}`
            ).join('\n');

            const prompt = `
You are an expert ECSA (Engineering Council of South Africa) assessor evaluating a candidate engineer's readiness for professional engineer registration.

Candidate Information:
- Name: ${userData?.full_name || user?.displayName || 'Not specified'}
- Discipline: ${userData?.discipline || 'Not specified'}
- Years of Experience: ${userData?.years_of_experience || 'Not specified'}

Work Experience Submission:
Title: ${workExperience.title}
Description: ${workExperience.description}

Your task is to assess this work experience against the 11 ECSA outcomes for Professional Engineer registration.

ECSA Outcomes:
${outcomesText}

Please provide:
1. A score (0-100) for each of the 11 outcomes based on the evidence provided
2. An overall assessment score (0-100)
3. A readiness level: "Not Ready" (<50), "Developing" (50-69), "Ready" (70-84), "Highly Ready" (85+)
4. 3-5 key strengths demonstrated
5. 3-5 areas for improvement
6. Detailed recommendations for achieving professional engineer status

Provide your response in the following JSON format:
{
  "outcome_scores": [
    {"outcome_number": 1, "score": 75, "evidence": "Brief explanation"},
    {"outcome_number": 2, "score": 80, "evidence": "Brief explanation"},
    {"outcome_number": 3, "score": 70, "evidence": "Brief explanation"},
    {"outcome_number": 4, "score": 65, "evidence": "Brief explanation"},
    {"outcome_number": 5, "score": 75, "evidence": "Brief explanation"},
    {"outcome_number": 6, "score": 85, "evidence": "Brief explanation"},
    {"outcome_number": 7, "score": 70, "evidence": "Brief explanation"},
    {"outcome_number": 8, "score": 80, "evidence": "Brief explanation"},
    {"outcome_number": 9, "score": 65, "evidence": "Brief explanation"},
    {"outcome_number": 10, "score": 75, "evidence": "Brief explanation"},
    {"outcome_number": 11, "score": 70, "evidence": "Brief explanation"}
  ],
  "overall_score": 73,
  "readiness_level": "Ready",
  "strengths": [
    "Strong problem-solving abilities",
    "Good communication skills",
    "Professional conduct"
  ],
  "areas_for_improvement": [
    "More detailed documentation",
    "Advanced technical knowledge"
  ],
  "recommendations": "Continue developing technical expertise and document achievements more thoroughly. Consider taking advanced courses in your discipline."
}
`;

            // Use Google Gemini
            const modelName = 'gemini-3.5-flash';

            // Prepare content for Gemini
            const contents = [
                { text: prompt }
            ];

            // If there's a file attachment, include it
            // if (workExperience.storagePath) {
            //     try {
            //         // Fetch file from storage
            //         const fileResponse = await fetch(`/api/get-file/${workExperience.id}`);
            //         if (fileResponse.ok) {
            //             const fileData = await fileResponse.blob();
            //             // Convert file to base64 for Gemini
            //             const reader = new FileReader();
            //             const base64Data = await new Promise((resolve) => {
            //                 reader.onload = () => resolve(reader.result);
            //                 reader.readAsDataURL(fileData);
            //             });
            //
            //             contents.push({
            //                 inlineData: {
            //                     mimeType: fileData.type,
            //                     data: base64Data.split(',')[1]
            //                 }
            //             });
            //         }
            //     } catch (error) {
            //         console.error('Error loading file for Gemini:', error);
            //     }
            // }

            const geminiResponse = await ai.models.generateContent({
                model: "gemini-3.5-flash",
                contents: contents,
                config: {
                    temperature: 0.7,
                    topP: 0.95,
                    topK: 40,
                    maxOutputTokens: 8192,
                }
            });

            // Parse the response
            let responseText = geminiResponse.text || '';

            // Extract JSON from response
            if (responseText.includes('```json')) {
                responseText = responseText.split('```json')[1].split('```')[0];
            } else if (responseText.includes('```')) {
                responseText = responseText.split('```')[1].split('```')[0];
            }

            // Clean up response text
            responseText = responseText.trim();

            // Try to parse JSON
            let result;
            try {
                result = JSON.parse(responseText);
            } catch (parseError) {
                console.error('JSON Parse Error:', parseError);
                console.log('Response text:', responseText);

                // Try to extract JSON using regex
                const jsonMatch = responseText.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    try {
                        result = JSON.parse(jsonMatch[0]);
                    } catch (e) {
                        throw new Error('Failed to parse assessment response');
                    }
                } else {
                    throw new Error('Invalid response format');
                }
            }

            // Validate the result structure
            if (!result.outcome_scores || !result.overall_score || !result.readiness_level) {
                throw new Error('Incomplete assessment data');
            }

            // Create assessment result object
            const assessment = {
                userId: user.uid,
                workExperienceId: workExperience.id,
                modelUsed: modelName,
                overallScore: result.overall_score,
                readinessLevel: result.readiness_level,
                outcomeScores: result.outcome_scores,
                strengths: result.strengths || [],
                areasForImprovement: result.areas_for_improvement || [],
                recommendations: result.recommendations || '',
                createdAt: new Date().toISOString()
            };

            return assessment;

        } catch (error) {
            console.error('AI assessment error:', error);
            throw new Error(`Assessment failed: ${error.message}`);
        }
    };

    const handleAssess = async () => {
        if (!workExp) {
            toast.error('Work experience not found');
            return;
        }

        setLoading(true);
        try {
            // Perform AI assessment with Gemini
            const assessment = await performAIAssessment(workExp, userData);

            // Store assessment result in Firestore
            const assessmentData = {
                userId: user.uid,
                workExpId: workExpId,
                workExpTitle: workExp.title,
                overall_score: assessment.overallScore,
                readiness_level: assessment.readinessLevel,
                model_used: assessment.modelUsed,
                outcome_scores: assessment.outcomeScores,
                strengths: assessment.strengths,
                areas_for_improvement: assessment.areasForImprovement,
                recommendations: assessment.recommendations,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                download_count: 0,
                view_count: 0
            };

            const assessmentRef = await addDoc(collection(db, 'assessments'), assessmentData);
            setAssessmentResult(assessment);

            // Update work experience with assessment reference
            const workExpRef = doc(db, 'workExperiences', workExpId);
            await updateDoc(workExpRef, {
                last_assessment_id: assessmentRef.id,
                last_assessed_at: serverTimestamp(),
                assessment_count: (workExp.assessment_count || 0) + 1
            });

            toast.success('Assessment completed successfully!');

            // Navigate to dashboard after a delay
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);

        } catch (error) {
            console.error('Assessment error:', error);
            toast.error(error.message || 'Assessment failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!workExp || checkingPayment) {
        return (
            <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="mt-4 text-[#666666]">Loading assessment data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F9F6]">
            <SEO title="Assessment | MyProfessionalPath"
                 description="myprofessionalpath is an online professional development system. The system is aligned with
                 the Engineering Council of South Africa for candidate engineers, technologists and technicians.  Progress
                 can be measured and tracked against the competence statements for the chosen registration category.
                 It can also be used for individual company CPD reporting.. The system is mobile and tablet compatible so
                 records can be accessed or added to on-the-go."
                 canonicalURL="https://myprofessionalpath.web.app" />
            <nav className="sticky-nav bg-white border-b border-[#DCDCDA]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn"
                                className="flex items-center gap-2">
                            <ArrowLeft size={18}/> Back to Dashboard
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <h1 className="font-heading text-4xl font-semibold text-[#0A0A0A] mb-3">Start AI Assessment</h1>
                    <p className="text-base text-[#666666]">Evaluate your work experience against the 11 ECSA outcomes
                        using Google Gemini AI.</p>
                </div>

                <Card className="swiss-card p-6 mb-8">
                    <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-2">{workExp.title}</h3>
                    <p className="text-sm text-[#666666] line-clamp-3">{workExp.description}</p>
                    {workExp.created_at && (
                        <p className="text-xs text-[#666666] mt-2">
                            Submitted: {formatDate(workExp.created_at)}
                        </p>
                    )}
                </Card>

                {!hasPaid ? (
                    <Card className="swiss-card p-8" data-testid="payment-required-card">
                        <div className="flex items-start gap-4 mb-6">
                            <div
                                className="w-12 h-12 bg-[#FF5A00]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                <CreditCard size={24} weight="duotone" className="text-[#FF5A00]"/>
                            </div>
                            <div className="flex-1">
                                <h3 className="font-heading text-xl font-medium text-[#0A0A0A] mb-2">Payment
                                    Required</h3>
                                <p className="text-sm text-[#666666]">
                                    Unlock AI-powered verification and detailed insights on your work experience against
                                    all 11 ECSA outcomes.
                                </p>
                            </div>
                        </div>

                        <div className="border border-[#DCDCDA] rounded-sm p-6 mb-6 bg-[#F9F9F6]">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#666666] mb-2">AI
                                ASSESSMENT</p>
                            <div className="flex items-baseline gap-2 mb-4">
                                <span
                                    className="font-heading text-4xl font-semibold text-[#0A0A0A]">R{pricing.assessment_price}</span>
                                <span className="text-sm text-[#666666]">/ assessment</span>
                            </div>
                            <ul className="space-y-2 text-sm text-[#666666]">
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill"
                                                 className="text-[#009944] flex-shrink-0 mt-0.5"/>
                                    <span>Full evaluation against all 11 ECSA outcomes</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill"
                                                 className="text-[#009944] flex-shrink-0 mt-0.5"/>
                                    <span>Detailed score for each outcome with evidence</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill"
                                                 className="text-[#009944] flex-shrink-0 mt-0.5"/>
                                    <span>Personalised strengths and improvement areas</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle size={16} weight="fill"
                                                 className="text-[#009944] flex-shrink-0 mt-0.5"/>
                                    <span>Powered by Google Gemini 3.1 Pro</span>
                                </li>
                            </ul>
                        </div>

                        <div
                            disabled={paying}
                            data-testid="pay-assessment-btn"
                            className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm py-6 text-base font-medium flex items-center justify-center gap-2"
                        >
                            <CreditCard size={20} weight="duotone"/>
                            {paying ? 'Processing payment...' : <PaystackButton {...componentProps} />}
                        </div>
                        <p className="text-xs text-center text-[#666666] mt-3">
                            Secure payment powered by Paystack. You'll be redirected to complete payment.
                        </p>
                    </Card>
                ) : (
                    <Card className="swiss-card p-8">
                        <div className="flex items-center gap-3 mb-6 p-3 bg-[#009944]/10 rounded-sm"
                             data-testid="payment-confirmed-banner">
                            <CheckCircle size={20} weight="fill" className="text-[#009944]"/>
                            <p className="text-sm font-medium text-[#0A0A0A]">Payment confirmed. Ready to run your
                                assessment.</p>
                        </div>

                        <div className="flex items-center gap-4 p-6 bg-[#F3F3F0] rounded-sm mb-6">
                            <div
                                className="w-12 h-12 bg-[#009944]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                <Sparkle size={24} weight="duotone" className="text-[#009944]"/>
                            </div>
                            <div>
                                <h3 className="font-heading text-lg font-medium text-[#0A0A0A] mb-1">Google Gemini 3.1
                                    Pro</h3>
                                <p className="text-sm text-[#666666]">Advanced AI model for comprehensive engineering
                                    assessment</p>
                            </div>
                        </div>

                        <div className="p-4 bg-[#F3F3F0] rounded-sm mb-6">
                            <p className="text-sm text-[#666666]">
                                <strong className="text-[#0A0A0A]">Note:</strong> The assessment will evaluate your work
                                experience against all 11 ECSA outcomes and provide detailed scores, strengths, areas
                                for improvement, and recommendations. This typically takes 30-60 seconds.
                            </p>
                        </div>

                        <Button
                            onClick={handleAssess}
                            disabled={loading}
                            data-testid="start-assessment-btn"
                            className="w-full bg-[#002FA7] hover:bg-[#0040D9] text-white rounded-sm py-6 text-base font-medium disabled:opacity-50"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <div
                                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Assessing... Please wait
                                </span>
                            ) : (
                                'Start Assessment'
                            )}
                        </Button>
                    </Card>
                )}
            </main>
        </div>
    );
};

export default AssessmentPage;